import { useState, useEffect } from 'react';

import { Avatar, Box, Link, Stack } from '@mui/material';
import { useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';
import useSWR from 'swr';

import { financeConfigurationAtom } from '~atoms';
import { AsyncTextField, Table, Label } from '~components';
import { useAuthorize } from '~features/authentication';
import { BillingsService, PaymentStatusEnum, PaymentStatusSelect } from '~features/finances';
import { getInitials } from '~utils/stringUtils';

import BillingPeriod from './BillingPeriod';
import { useStyles } from './style';
import { isFullArray, decimalAmount, getFinanceTableHeaders } from '../../../../../shared/utility';

const service = new BillingsService();

interface BillingsUsersProps {
	financeType?: string;
	date?: string;
	id?: string;
	setFullName?(...args: unknown[]): unknown;
	name?: string;
	title?: string;
}

const BillingsUsers = (props: BillingsUsersProps) => {
	const { date, setFullName, id, name, title } = props;
	const { t } = useTranslation();
	const { isSuperAdmin } = useAuthorize();
	const financeConfiguration = useAtomValue(financeConfigurationAtom);

	const classes = useStyles();
	const [searchValue, setSearchValue] = useState('');
	const [paymentStatusFilter, setPaymentStatusFilter] = useState<PaymentStatusEnum | null>(null);

	const { data, isLoading } = useSWR(
		[
			service.basePath,
			{
				date: date,
				searchQuery: searchValue || undefined,
				paymentStatus: paymentStatusFilter ?? undefined,
				organisationId: id,
				excludeOneOffPayments: financeConfiguration.excludeOneOffPayments,
			},
		],
		([_, args]) => service.getBillings(args)
	);

	useEffect(() => {
		if (data != null) {
			setFullName(data.title);
		}
	}, [data]);

	const statusColor = {
		open: 'default',
		paid: 'success',
	};

	const statusText = {
		open: t('ui.open'),
		paid: t('ui.label.paid'),
	};

	const headers = getFinanceTableHeaders(true);

	const tableHeader = [
		{ name: 'users', content: t('ui.category.users') },
		{ name: 'userId', content: `${t('ui.label.user')} id` },
		...headers,
	];

	const emptyBody =
		!isLoading && data?.billings?.length <= 0 ?
			Array(1)
				.fill(Array(tableHeader.length).fill())
				.map((arr) => arr.map(() => ({ content: '-' })))
		:	null;

	const loadingBody =
		data == null || isLoading ?
			Array(4)
				.fill(Array(tableHeader.length).fill())
				.map((arr) => arr.map(() => ({ loading: true })))
		:	null;

	const dataBody =
		data != null && !isLoading ?
			data.billings.map((user) => [
				{
					content:
						isFullArray(user.userReference.imagesReference) ?
							<Box className={classes.cell}>
								<Avatar src={user.userReference.imagesReference[0]} />
								<Box pl={1.5}>
									<Link
										color='inherit'
										component={RouterLink}
										to={`/financeManagement/revenue/${user.start}/${title}/partner/${id}/${encodeURIComponent(name ?? '')}/parsonal/bookings`}
										state={{ data: user }}
									>
										{user.userReference.name}
									</Link>
								</Box>
							</Box>
						:	<Box className={classes.cell}>
								<Avatar>{getInitials(user.userReference.name)}</Avatar>
								<Box pl={1}>
									<Link
										color='inherit'
										component={RouterLink}
										to={`/financeManagement/revenue/${user.start}/${title}/partner/${id}/${encodeURIComponent(name ?? '')}/parsonal/bookings`}
										state={{ data: user }}
									>
										{user.userReference.name}
									</Link>
								</Box>
							</Box>,
				},
				{ content: user.userReference.id },
				{ content: decimalAmount(user.amount.subtotal, user.amount) },
				{ content: decimalAmount(user.amount.discount, user.amount) },
				{ content: decimalAmount(user.amount.subtotal - user.amount.discount, user.amount) },
				{ content: decimalAmount(user.amount.priceVat, user.amount) },
				{ content: decimalAmount(user.amount.refund, user.amount) },
				{ content: decimalAmount(user.amount.totalPrice, user.amount) },
				{ content: decimalAmount(user.open, user.amount) },
				{
					content: <Label type={statusColor[user.status]}>{statusText[user.status]}</Label>,
				},
			])
		:	Array(10)
				.fill(Array(tableHeader.length).fill())
				.map((arr) => arr.map(() => ({ loading: true })));

	return (
		<>
			<Box pt={3}>
				<BillingPeriod data={data} loading={isLoading} />
				<Stack spacing={1} direction='row' sx={{ pb: 3 }}>
					<AsyncTextField
						manual
						placeholder={t('views.usersAndOrganisations.organisations.searchPlaceholder')}
						defaultValue={searchValue}
						onChange={(e) => setSearchValue(e.target.value)}
						size='small'
						sx={{ width: 250 }}
					/>
					<PaymentStatusSelect
						value={paymentStatusFilter}
						onChange={(e) => setPaymentStatusFilter(e.target.value)}
						displayEmpty
						size='small'
						sx={{ width: 200 }}
					/>
				</Stack>
			</Box>
			<Table
				body={loadingBody || emptyBody || dataBody}
				cellStyle={classes.cellStyle}
				data={data?.billings ?? []}
				header={tableHeader}
				// page={pageNumber}
				// rowsPerPage={pageSize}
				isNotPaginate={true}
				loading={isLoading}
				noTitle={true}
				total={data?.total ?? 0}
				withoutPages={true}
			/>
		</>
	);
};

const mapStateToProps = (state) => {
	return {
		personalRevenue: state.details.personalRevenue,
	};
};

export default connect(mapStateToProps)(BillingsUsers);
